import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, max, min, required, validate } from '@angular/forms/signals';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { firstValueFrom } from 'rxjs';

import { UpdateSystemConfigurationRequestV1 } from '@access-management/configuration/system-configuration';
import { SystemConfigurationService } from '@core/services/system-configuration';

interface ConfigurationFormModel {
  deviceEligibilityMinAge: number;
  hipaaConsentMinAge: number;
  signupMinAge: number;
}

@Component({
  selector: 'yt-configuration',
  standalone: true,
  imports: [FormsModule, CardModule, InputNumberModule, ButtonModule, ProgressSpinnerModule],
  templateUrl: './configuration.html',
  styleUrl: './configuration.scss'
})
export class Configuration {
  private readonly configService = inject(SystemConfigurationService);
  private readonly messageService = inject(MessageService);

  readonly configResource = this.configService.createSystemConfigurationResource();
  readonly saving = signal<boolean>(false);
  readonly isLoading = computed(() => this.configResource.isLoading());

  readonly formModel = signal<ConfigurationFormModel>({
    deviceEligibilityMinAge: 0,
    hipaaConsentMinAge: 13,
    signupMinAge: 18
  });

  readonly configForm = form(this.formModel, (path) => {
    required(path.deviceEligibilityMinAge, { message: 'Device eligibility minimum age is required' });
    min(path.deviceEligibilityMinAge, 0, { message: 'Must be 0 or greater' });
    max(path.deviceEligibilityMinAge, 21, { message: 'Must be 21 or less' });
    validate(path.deviceEligibilityMinAge, ({ value }) =>
      Number.isInteger(value()) ? null : { kind: 'notInteger', message: 'Must be a whole number' }
    );

    required(path.hipaaConsentMinAge, { message: 'HIPAA consent minimum age is required' });
    min(path.hipaaConsentMinAge, 0, { message: 'Must be 0 or greater' });
    max(path.hipaaConsentMinAge, 21, { message: 'Must be 21 or less' });
    validate(path.hipaaConsentMinAge, ({ value }) =>
      Number.isInteger(value()) ? null : { kind: 'notInteger', message: 'Must be a whole number' }
    );

    required(path.signupMinAge, { message: 'Signup minimum age is required' });
    min(path.signupMinAge, 0, { message: 'Must be 0 or greater' });
    max(path.signupMinAge, 21, { message: 'Must be 21 or less' });
    validate(path.signupMinAge, ({ value }) =>
      Number.isInteger(value()) ? null : { kind: 'notInteger', message: 'Must be a whole number' }
    );
  });

  constructor() {
    effect(() => {
      const config = this.configResource.value();
      if (!config) {
        return;
      }
      untracked(() => {
        this.formModel.set({
          deviceEligibilityMinAge: config.deviceEligibilityMinimumAge,
          hipaaConsentMinAge: config.hipaaConsentMinimumAge,
          signupMinAge: config.signupMinimumAge
        });
      });
    });
  }

  async onSave(): Promise<void> {
    if (this.configForm().invalid()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation',
        detail: 'Please fix the errors before saving'
      });
      return;
    }

    this.saving.set(true);
    const model = this.formModel();
    const request: UpdateSystemConfigurationRequestV1 = {
      deviceEligibilityMinimumAge: model.deviceEligibilityMinAge,
      hipaaConsentMinimumAge: model.hipaaConsentMinAge,
      signupMinimumAge: model.signupMinAge
    };

    try {
      await firstValueFrom(this.configService.updateSystemConfiguration(request));
      this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'System configuration updated' });
      this.configResource.reload();
    } catch (error) {
      console.error('[Configuration] save failed', error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save system configuration' });
    } finally {
      this.saving.set(false);
    }
  }
}
